import React from 'react';
import { motion } from 'framer-motion';

const OrdersList = ({ orders, onStatusChange }) => {
  const statusColors = {
    pending: 'bg-yellow-500/20 text-yellow-300 border-yellow-500/50',
    processing: 'bg-blue-500/20 text-blue-300 border-blue-500/50',
    completed: 'bg-green-500/20 text-green-300 border-green-500/50',
    cancelled: 'bg-red-500/20 text-red-300 border-red-500/50',
  };

  if (orders.length === 0) {
    return (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        className="text-center py-12 card p-8 bg-white/10 border border-white/20"
      >
        <p className="text-gray-300 text-lg">No orders yet</p>
      </motion.div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="space-y-4"
    >
      {orders.map((order, idx) => (
        <motion.div
          key={order.order_id}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: idx * 0.1 }}
          className="card p-6 bg-white/10 border border-white/20 backdrop-blur-xl"
        >
          <div className="grid md:grid-cols-2 gap-6">
            {/* Left column */}
            <div className="space-y-3">
              <div>
                <p className="text-gray-400 text-sm">Order ID</p>
                <p className="text-white font-mono text-sm break-all">{order.order_id}</p>
              </div>
              <div>
                <p className="text-gray-400 text-sm">Customer Name</p>
                <p className="text-white font-semibold">{order.full_name}</p>
              </div>
              <div>
                <p className="text-gray-400 text-sm">Email</p>
                <p className="text-blue-300 text-sm break-all">{order.email}</p>
              </div>
              <div>
                <p className="text-gray-400 text-sm">Phone</p>
                <p className="text-white">{order.phone}</p>
              </div>
            </div>

            {/* Right column */}
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-gray-400 text-sm">Currency Type</p>
                  <p className="text-white font-semibold">{order.crypto_type}</p>
                </div>
                <div>
                  <p className="text-gray-400 text-sm">Amount</p>
                  <p className="text-white font-semibold">{order.amount} {order.crypto_type}</p>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-gray-400 text-sm">Estimated Price</p>
                  <p className="text-white font-semibold">${parseFloat(order.estimated_price).toFixed(2)}</p>
                </div>
                <div>
                  <p className="text-gray-400 text-sm">Commission (10%)</p>
                  <p className="text-yellow-300 font-semibold">${parseFloat(order.commission).toFixed(2)}</p>
                </div>
              </div>
              <div>
                <p className="text-gray-400 text-sm">Total Amount</p>
                <p className="text-white font-bold text-lg">${parseFloat(order.total_amount).toFixed(2)}</p>
              </div>
            </div>
          </div>

          {/* Status and Actions */}
          <div className="mt-6 pt-6 border-t border-white/20 flex flex-col sm:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <p className="text-gray-400 text-sm">Status:</p>
              <span className={`px-4 py-2 rounded-full text-sm font-semibold border ${statusColors[order.status] || statusColors.pending}`}>
                {order.status.charAt(0).toUpperCase() + order.status.slice(1)}
              </span>
            </div>

            <div className="flex gap-2">
              {order.status !== 'completed' && order.status !== 'cancelled' && (
                <>
                  {order.status === 'pending' && (
                    <motion.button
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      onClick={() => onStatusChange(order.order_id, 'processing')}
                      className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors text-sm font-semibold"
                    >
                      Process
                    </motion.button>
                  )}
                  {order.status === 'processing' && (
                    <motion.button
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      onClick={() => onStatusChange(order.order_id, 'completed')}
                      className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 transition-colors text-sm font-semibold"
                    >
                      Complete
                    </motion.button>
                  )}
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={() => onStatusChange(order.order_id, 'cancelled')}
                    className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 transition-colors text-sm font-semibold"
                  >
                    Cancel
                  </motion.button>
                </>
              )}
            </div>
          </div>

          <p className="text-gray-500 text-xs mt-4">
            Created: {new Date(order.created_at).toLocaleString()}
          </p>
        </motion.div>
      ))}
    </motion.div>
  );
};

export default OrdersList;
